import { Checkbox, CheckboxProps } from '@mui/material'
import { ChangeEvent, FC } from 'react'

// Re-declare commonly used props to make them visible to the generator
export type C1CheckboxProps = Omit<CheckboxProps, 'variant'> & {
    /** Whether the checkbox is in the checked state. */
    checked?: boolean
    /** Callback fired when the checked state changes. Receives the event and the new checked value. */
    onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
    /** If `true`, the checkbox is disabled and non-interactive. */
    disabled?: boolean
    /** If `true`, displays an indeterminate icon instead of checked/unchecked. Useful for "select all" headers where only some items are selected. Does not affect the `checked` value. */
    indeterminate?: boolean
    /** The color of the checkbox when checked or indeterminate. Defaults to `primary`. */
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default'
    /** The size of the checkbox. `small` reduces padding and icon size. Defaults to `medium`. */
    size?: 'small' | 'medium'
}

/**
 * Checkbox input for toggling boolean values or making multi-select choices.
 *
 * Thin wrapper around MUI Checkbox that removes the `variant` prop to enforce
 * consistent styling across the product. All other MUI Checkbox props pass through.
 *
 * ## When to use
 * - **Boolean fields** — opt-in/opt-out settings, terms acceptance, feature toggles in forms
 * - **Multi-select lists** — selecting multiple items from a set (e.g. bulk actions on a table)
 * - **Select all** — use `indeterminate` on a header checkbox when only some rows are selected
 *
 * ## Colors
 * - `primary` — default, use for most form checkboxes
 * - `secondary` — use to distinguish from primary-colored controls nearby
 * - `error` — use for destructive opt-ins (e.g. "permanently delete")
 * - `success` — use for positive confirmations
 *
 * ## Behavior
 * - The `indeterminate` prop is purely visual — it does not change the underlying
 *   `checked` value. You must manage both `checked` and `indeterminate` independently.
 * - Wrap with `FormControlLabel` to add a clickable text label alongside the checkbox.
 * - For form integration, use `C1FormCheckbox` which connects to `react-hook-form` via `Controller`.
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1Switch | Use for boolean toggles with immediate effect (e.g. enabling a setting)
 * @relatedComponent C1Radio | Use for single selection from a group of mutually exclusive options
 * @externalReference MUI Checkbox API | https://mui.com/material-ui/api/checkbox/
 */
export const C1Checkbox: FC<C1CheckboxProps> = (props) => <Checkbox {...props} />
